import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
} from 'victory';
import { RSIItem } from '../../../screens/ProfitCalculations/types';
import { useMemo } from 'react';
import { XAxisInfo } from '../../MultiChart';
import IndicatorLabel from '../IndicatorLabel';

interface Props {
  rsi?: RSIItem[];
  waver?: RSIItem[][];
  overExtensionThreshold?: number | null;
  label: string;

  width?: number | undefined;
  height?: number | undefined;
  xAxisInfo?: XAxisInfo | undefined;
  showXAxis?: boolean;
}

const RSIChart = (props: Props) => {
  const {
    width,
    height,
    label,
    overExtensionThreshold = 75,
    xAxisInfo,
    showXAxis,
  } = props;

  const data = useMemo(() => {
    return props.rsi;
  }, [props.rsi]);

  const waverData = useMemo(() => {
    return props.waver;
  }, [props.waver]);

  const dataPoints = useMemo(() => {
    if (!data || data?.length < 2) {
      return [];
    }

    /** Because the candles start at the beginning of the candle
     * and the rsi's value is al the end of the candle
     * We have to change position a little
     */
    const range =
      data[data.length - 1].timestamp - data[data.length - 2].timestamp;
    return data.map((d) => ({ x: d.timestamp - range, y: d.value })) ?? [];
  }, [data]);

  const waverDataDataSets = useMemo(() => {
    if (!waverData?.length || !waverData[0]?.length || !waverData[0]?.[0]) {
      return [];
    }

    /** Because the candles start at the beginning of the candle
     * and the rsi's value is at the end of the candle
     * We have to change position a little
     */
    const range = waverData[0][0].timestamp - waverData[0][1].timestamp;

    return waverData?.reduce((acc, curr) => {
      acc.push(curr.map((d) => ({ x: d.timestamp - range, y: d.value })));
      return acc;
    }, [] as Array<{ x: number; y: number }[]>);
  }, [waverData]);

  const yAxisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: { stroke: 'white', size: 2 },
      tickLabels: { fontSize: 14, padding: 5, fill: '#999' },
    }),
    [],
  );

  const yTickValues = useMemo(() => {
    if (overExtensionThreshold) {
      return [0, 100 - overExtensionThreshold, 50, overExtensionThreshold, 100];
    }

    return [0, 25, 50, 75, 100];
  }, [overExtensionThreshold]);

  const overExtensionRangeDataPoints = useMemo(() => {
    if (!overExtensionThreshold || !xAxisInfo?.domain.x.length) {
      return [];
    }

    return [
      {
        x: xAxisInfo?.domain.x[0],
        y: overExtensionThreshold,
        y0: 100 - overExtensionThreshold,
      },
      {
        x: xAxisInfo?.domain.x[1],
        y: overExtensionThreshold,
        y0: 100 - overExtensionThreshold,
      },
    ];
  }, [overExtensionThreshold, xAxisInfo?.domain.x]);

  const gradientTopOffset = useMemo(() => {
    if (!overExtensionThreshold) {
      return 0;
    }

    const max = Math.max(
      ...dataPoints.map((d) => d.y),
      ...waverDataDataSets.reduce(
        (acc, curr) => [...acc, ...curr.map((d) => d.y)],
        [] as number[],
      ),
    );
    const min = Math.min(
      ...dataPoints.map((d) => d.y),
      ...waverDataDataSets.reduce(
        (acc, curr) => [...acc, ...curr.map((d) => d.y)],
        [] as number[],
      ),
    );
    const domainYRange = max - min;
    const topOffset = (max - overExtensionThreshold) / domainYRange;

    return topOffset;
  }, [dataPoints, overExtensionThreshold, waverDataDataSets]);

  const gradientBottomOffset = useMemo(() => {
    if (!overExtensionThreshold) {
      return 0;
    }

    const max = Math.max(
      ...dataPoints.map((d) => d.y),
      ...waverDataDataSets.reduce(
        (acc, curr) => [...acc, ...curr.map((d) => d.y)],
        [] as number[],
      ),
    );
    const min = Math.min(
      ...dataPoints.map((d) => d.y),
      ...waverDataDataSets.reduce(
        (acc, curr) => [...acc, ...curr.map((d) => d.y)],
        [] as number[],
      ),
    );
    const domainYRange = max - min;
    const topOffset = 1 - (100 - overExtensionThreshold - min) / domainYRange;

    return topOffset;
  }, [dataPoints, overExtensionThreshold, waverDataDataSets]);

  const chartWidth = width;
  const chartHeight = height || 120;
  const gradientId = `RSI_gradient_${Math.random()}`;

  return (
    <div
      className="candlesticks-container"
      style={{
        width: chartWidth,
        height: chartHeight,
      }}
    >
      <IndicatorLabel label={label} />
      <svg style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0" stopColor="green" />
            <stop offset={gradientTopOffset} stopColor="gray" />
            <stop offset={gradientBottomOffset} stopColor="gray" />
            <stop offset="1" stopColor="red" />
          </linearGradient>
        </defs>
      </svg>
      <VictoryChart
        width={chartWidth}
        domain={{ ...xAxisInfo?.domain, y: [-10, 105] }}
        domainPadding={xAxisInfo?.domainPadding}
        height={chartHeight}
        padding={{
          ...xAxisInfo?.chartPadding,
          top: 0,
          bottom: showXAxis ? 30 : 0,
        }}
      >
        <VictoryAxis
          tickFormat={showXAxis ? xAxisInfo?.tickFormat : () => ''}
          style={{ ...xAxisInfo?.axisStyle, ...(!showXAxis && { ticks: {} }) }}
          tickValues={xAxisInfo?.tickValues}
        />
        <VictoryAxis
          dependentAxis
          style={yAxisStyle}
          tickFormat={(t) => `${t}%`}
          orientation={'right'}
          tickLabelComponent={<VictoryLabel dx={5} />}
          tickValues={yTickValues}
        />

        {overExtensionRangeDataPoints && (
          <VictoryArea
            data={overExtensionRangeDataPoints}
            style={{
              data: {
                fill: '#FFC70020',
              },
            }}
          />
        )}

        <VictoryLine
          data={dataPoints}
          style={{
            data: { stroke: `url(#${gradientId})` },
          }}
        />
        {waverDataDataSets?.map((ds, index) => (
          <VictoryLine
            key={`waver_${index}`}
            data={ds}
            style={{
              data: { stroke: `url(#${gradientId})` },
            }}
          />
        ))}
      </VictoryChart>
    </div>
  );
};

export default RSIChart;
